import { useState } from 'react';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { newIdempotencyKey } from '../../lib/api';
import { describeApiError } from './errors';
import { itemsOf, queryString, useApiQuery } from './hooks';
import { memberLabel, RETIRE_REASONS, SUSPEND_REASONS, type Reason } from './states';
import type { Member, QrDetail } from './types';
import { useOrgApi } from './useOrgApi';

export type LifecycleAction = 'suspend' | 'reactivate' | 'deactivate' | 'retire';

interface Props {
  action: LifecycleAction;
  qr: QrDetail;
  onClose: () => void;
  onDone: (next: Partial<QrDetail>) => void;
}

function ReasonSelect({ id, reasons, value, onChange, error }: { id: string; reasons: Reason[]; value: string; onChange: (v: string) => void; error?: string }) {
  return (
    <div className="flex flex-col gap-1">
      <label htmlFor={id} className="text-xs font-medium text-ink-muted">
        Motivo (obligatorio)
      </label>
      <select id={id} value={value} onChange={(e) => onChange(e.target.value)} aria-invalid={error ? true : undefined} className={`w-full rounded-sm border bg-white px-3 py-2 text-sm ${error ? 'border-error' : 'border-line'}`}>
        <option value="">Elige un motivo</option>
        {reasons.map((r) => (
          <option key={r.value} value={r.value}>
            {r.label}
          </option>
        ))}
      </select>
      {error && <p role="alert" className="text-xs font-medium text-error-text">{error}</p>}
    </div>
  );
}

export function LifecycleModal({ action, qr, onClose, onDone }: Props) {
  const client = useOrgApi();
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [confirm, setConfirm] = useState('');
  const [custodianUid, setCustodianUid] = useState('');
  const [tried, setTried] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const members = useApiQuery(
    (signal) => (action === 'deactivate' ? client.get<{ items: Member[] }>('/v1/members', { signal }).then(itemsOf) : Promise.resolve([] as Member[])),
    [client, action],
  );

  const needsReason = action === 'suspend' || action === 'retire';
  const confirmOk = action !== 'retire' || confirm.trim().toUpperCase() === qr.code.toUpperCase();
  const custodianOk = action !== 'deactivate' || Boolean(custodianUid);

  async function submit() {
    setTried(true);
    setError(null);
    if ((needsReason && !reason) || !confirmOk || !custodianOk) return;
    setSaving(true);
    const body =
      action === 'suspend' || action === 'retire'
        ? { reason, note: note.trim() || undefined }
        : action === 'deactivate'
          ? { custodianUid }
          : {};
    try {
      const next = await client.post<Partial<QrDetail>>(
        `/v1/qrs/${encodeURIComponent(qr.code)}/${action}${queryString({ domain: qr.domain !== 'q-r.cl' ? qr.domain : undefined })}`,
        body,
        { idempotencyKey: newIdempotencyKey() },
      );
      onDone(next ?? {});
      onClose();
    } catch (err) {
      setError(describeApiError(err, 'No pudimos aplicar el cambio.'));
    } finally {
      setSaving(false);
    }
  }

  const titles: Record<LifecycleAction, string> = {
    suspend: `Suspender ${qr.code}`,
    reactivate: `Reactivar ${qr.code}`,
    deactivate: `Desactivar ${qr.code} para reasignar`,
    retire: `Dar de baja ${qr.code}`,
  };
  const cta: Record<LifecycleAction, string> = {
    suspend: 'Suspender',
    reactivate: 'Reactivar',
    deactivate: 'Desactivar',
    retire: 'Dar de baja definitivamente',
  };

  return (
    <Modal
      open
      onClose={onClose}
      title={titles[action]}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancelar</Button>
          <Button variant={action === 'retire' ? 'danger' : 'primary'} onClick={() => void submit()} loading={saving} disabled={action === 'retire' && !confirmOk}>
            {cta[action]}
          </Button>
        </>
      }
    >
      {action === 'suspend' && (
        <>
          <p className="text-sm text-gray-700">Mientras esté suspendido, quien escanee verá la página del partner. Puedes reactivarlo con el mismo destino.</p>
          <ReasonSelect id="suspend-reason" reasons={SUSPEND_REASONS} value={reason} onChange={setReason} error={tried && !reason ? 'Indica un motivo para suspender.' : undefined} />
          <Input label="Detalle (opcional)" value={note} maxLength={200} onChange={(e) => setNote(e.target.value)} />
        </>
      )}
      {action === 'reactivate' && (
        <p className="text-sm text-gray-700">
          Vuelve a llevar a <span className="break-all font-medium">{qr.targetUrl ?? 'su destino'}</span> desde el próximo escaneo.
        </p>
      )}
      {action === 'deactivate' && (
        <>
          <p className="text-sm text-gray-700">
            Vuelve a <b>en custodia</b>. Mostrará la página del partner hasta su próxima activación. El historial y los escaneos{qr.business?.name ? ` de «${qr.business.name}»` : ''} se conservan.
          </p>
          <div className="flex flex-col gap-1">
            <label htmlFor="deactivate-custodian" className="text-xs font-medium text-ink-muted">Nuevo custodio</label>
            <select id="deactivate-custodian" value={custodianUid} onChange={(e) => setCustodianUid(e.target.value)} className="w-full rounded-sm border border-line bg-white px-3 py-2 text-sm">
              <option value="">{members.loading ? 'Cargando miembros…' : 'Elige un miembro'}</option>
              {(members.data ?? []).filter((m) => m.role !== 'viewer').map((m) => (
                <option key={m.uid} value={m.uid}>{memberLabel(m)}</option>
              ))}
            </select>
            {tried && !custodianUid && <p role="alert" className="text-xs font-medium text-error-text">Elige quién queda con el QR.</p>}
          </div>
        </>
      )}
      {action === 'retire' && (
        <>
          <Banner tone="error">
            <b>Esta acción es irreversible.</b> El código se retira para siempre; sus escaneos mostrarán «código no activo».
          </Banner>
          <ReasonSelect id="retire-reason" reasons={RETIRE_REASONS} value={reason} onChange={setReason} error={tried && !reason ? 'Indica un motivo para dar de baja.' : undefined} />
          <Input label={`Escribe ${qr.code} para confirmar`} mono value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder={qr.code} autoComplete="off" />
        </>
      )}
      {error && <Banner tone="error">{error}</Banner>}
    </Modal>
  );
}
